import { BrowserDocumentServiceInterface } from '../../service/browser-document/service.interface';
import { DataKeyValueStringInterface } from '../data/key-value/string.interface';
import { BrowserOpenLinkPropsInterface } from './open-link-props.interface';

/**
 * Handles window.document state/modifications
 */
export class BrowserDocument implements BrowserDocumentServiceInterface {
  /**
   * CSS classes
   */
  protected cssClass: DataKeyValueStringInterface = {
    noScroll: 'body-noscroll',
  };

  /**
   * @param document
   */
  constructor(private document: HTMLDocument) {}

  /**
   * @inheritDoc
   */
  public getDocument(): HTMLDocument {
    return this.document;
  }

  /**
   * @inheritDoc
   */
  public getHead(): HTMLHeadElement {
    return this.document.head || this.document.getElementsByTagName('head')[0];
  }

  /**
   * @inheritDoc
   */
  public setScrollable(scrollable: boolean): void {
    if (scrollable) {
      this.document.body.classList.remove(this.cssClass.noScroll);
    } else {
      this.document.body.classList.add(this.cssClass.noScroll);
    }
  }

  /**
   * @inheritDoc
   */
  public importStylesheet(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const head = this.getHead();
      const link = this.document.createElement('link');

      // Prepare stylesheet
      link.rel = 'stylesheet';
      link.media = 'all';
      link.onerror = () => reject();
      link.onload = () => resolve();

      // Load stylesheet
      head.appendChild(link);
      link.href = src;
    });
  }

  /**
   * @inheritDoc
   */
  public importScript(src: string, isAsync?: boolean): Promise<void> {
    return new Promise((resolve, reject) => {
      const head = this.getHead();
      const script = this.document.createElement('script');

      // Prepare script
      script.type = 'text/javascript';
      script.onerror = () => reject();
      script.onload = () => resolve();

      // Add async attribute to the script
      if (isAsync) {
        script.setAttribute('async', '');
      }

      // Load script
      head.appendChild(script);
      script.src = src;
    });
  }

  /**
   * @inheritDoc
   */
  public setTitle(title: string): void {
    this.document.title = title;
  }

  /**
   * @inheritdoc
   */
  public openLink(props: BrowserOpenLinkPropsInterface): void {
    const a = this.document.createElement('a');
    a.href = props.href;
    a.target = props.target || '_self';
    a.rel = props.rel || '';

    a.click();
  }
}
